<template>
  <div id="app">
    <h1>Restaurant Picker</h1>
    <div>
      <label for="meal-option-select">Meal Option:</label>
      <select id="meal-option-select" v-model="selectedMealOption">
        <option value="">All Meals</option>
        <option value="Breakfast">Breakfast</option>
        <option value="Dinner">Dinner</option>
      </select>
    </div>
    <div>
      <label for="category-select">Categories:</label>
      <select id="category-select" v-model="selectedTypes" multiple>
        <option v-for="category in categories" :key="category" :value="category">{{ category }}</option>
      </select>
    </div>
    <div>
      <label for="city-select">City:</label>
      <select id="city-select" v-model="selectedCity">
        <option value="">All Cities</option>
        <option value="Columbus">Columbus</option>
        <option value="Cincinnati">Cincinnati</option>
      </select>
    </div>
    <div>
      <label for="visited-filter">Visited:</label>
      <select id="visited-filter" v-model="selectedVisited">
        <option value="">All</option>
        <option value="visited">Visited</option>
        <option value="notVisited">Not Visited</option>
      </select>
    </div>
    <button @click="selectRandomRestaurant">Choose!</button>
    <transition name="fade" @before-enter="beforeEnter" @enter="enter" @leave="leave">
      <div v-if="selectedRestaurant" id="content-wrapper">
        <div style="width: 100%; height: 500px">
          <div class="name-and-visited">
            <h2 class="restaurant-name">{{ selectedRestaurant.name }}</h2>
            <div class="visited-status" :class="{'is-visited': selectedRestaurant.visited}">
              {{ selectedRestaurant.visited ? 'Visited' : 'Not Visited Yet' }}
            </div>
          </div>
          <p class="restaurant-details">{{ selectedRestaurant.address }}</p>
          <p class="restaurant-details">{{ selectedRestaurant.city }}</p>
          <div class="restaurant-categories">
            Categories: <span>{{ formattedCategories }}</span>
          </div>
          <br>
        <div v-if="selectedRestaurant.rating !== undefined && selectedRestaurant.rating !== null" class="restaurant-rating">
          Rating: {{ selectedRestaurant.rating }} stars
        </div>
        <a :href="selectedRestaurant.websiteUrl" target="_blank" class="website-link">Visit Website</a>
          <GmapMap
            v-if="showMap"
            :center="mapCenter"
            :zoom="12"
            style="width: 100%; height: 300px; margin-top: 20px">
            <GmapMarker :position="mapCenter"/>
          </GmapMap>
        </div>
      </div>
    </transition>
    <h2>Possibilities</h2>
    <ul>
      <li v-for="restaurant in filteredRestaurants" :key="restaurant.name" :class="{'visited': restaurant.visited, 'not-visited': !restaurant.visited}">
        <h3>{{ restaurant.name }}</h3>
        <p>{{ restaurant.city }}</p>
        <div>
          Categories: 
          <span class="category" v-for="category in restaurant.categories.join(', ')" :key="category">{{ category }}</span>
        </div>
        <!-- Other details -->
      </li>
    </ul>

  </div>
</template>

<script>
import { getGoogleMapsAPI } from 'gmap-vue';

export default {
  data() {
    return {
      restaurants: [],
      selectedType: '',
      selectedCity: '',
      selectedVisited: '',
      selectedRestaurant: null,
      mapCenter: null,
      showMap: false,
      lastSelectedRestaurant: null,
      selectedTypes: [],
      selectedMealOption: '',
    };
  },
  watch: {
    selectedType() {
      this.clearSelectedRestaurant();
    },
    selectedCity() {
      this.clearSelectedRestaurant();
    },
    selectedVisited() {
      this.clearSelectedRestaurant();
    },
  },
  computed: {
    formattedCategories() {
      if (this.selectedRestaurant && this.selectedRestaurant.categories) {
        return this.selectedRestaurant.categories.join(', ');
      }
      return '';
    },
    filteredRestaurants() {
    return this.restaurants.filter(restaurant => {
      const matchesType = !this.selectedTypes.length || this.selectedTypes.some(type => restaurant.categories.includes(type));
      const matchesCity = !this.selectedCity || restaurant.city === this.selectedCity;
      const matchesVisited = this.selectedVisited === '' || (this.selectedVisited === 'visited' && restaurant.visited) || (this.selectedVisited === 'notVisited' && !restaurant.visited);
      const matchesMealOption = !this.selectedMealOption || restaurant.mealOptions.includes(this.selectedMealOption);

      return matchesType && matchesCity && matchesVisited && matchesMealOption;
    });
  },
    google: getGoogleMapsAPI,
    categories() {
      const allCategories = this.restaurants.flatMap(restaurant => restaurant.categories);
      return [...new Set(allCategories)]; // Removes duplicates
    },
    types() {
      return [...new Set(this.restaurants.map(restaurant => restaurant.type))];
    },
  },
  methods: {
    beforeEnter(el) {
      el.style.opacity = 0;
      el.style.height = '0';
    },
    enter(el, done) {
      el.style.transition = 'opacity 0.5s ease, height 0.5s ease';
      el.style.height = el.scrollHeight + 'px';
      el.style.opacity = 1;
      done();
    },
    leave(el, done) {
      el.style.transition = 'opacity 0.5s ease, height 0.5s ease';
      el.style.height = '0';
      el.style.opacity = 0;
      done();
    },
    clearSelectedRestaurant() {
      this.selectedRestaurant = null;
    },
    geocodeAddress(address) {
      this.$gmapApiPromiseLazy().then(() => {
        let geocoder = new this.google.maps.Geocoder();
        return new Promise((resolve, reject) => {
        geocoder.geocode({ 'address': address }, (results, status) => {
          if (status === this.google.maps.GeocoderStatus.OK) {
            resolve(results[0].geometry.location);
          } else {
            reject('Geocode was not successful for the following reason: ' + status);
          }
        });
      });
      });
    },
    selectRandomRestaurant() {
      if (this.filteredRestaurants.length === 0) {
        alert('No restaurants match your filters!');
        return;
      }

      // If there's only one restaurant in the filtered list and it was the last selected one, notify the user.
      if (this.filteredRestaurants.length === 1 && this.filteredRestaurants[0] === this.lastSelectedRestaurant) {
        alert('There\'s only one restaurant that matches your filters. Try changing the filters for more options.');
        return;
      }

      let randomIndex;
      let selectedRestaurant;

      do {
        randomIndex = Math.floor(Math.random() * this.filteredRestaurants.length);
        selectedRestaurant = this.filteredRestaurants[randomIndex];
      } while (selectedRestaurant === this.lastSelectedRestaurant && this.filteredRestaurants.length > 1);

      this.selectedRestaurant = selectedRestaurant;
      this.lastSelectedRestaurant = selectedRestaurant;
      this.$gmapApiPromiseLazy().then(() => {
        let geocoder = new this.google.maps.Geocoder();
        geocoder.geocode({ 'address': this.selectedRestaurant.address }, (results) => {
          this.mapCenter = results[0].geometry.location
          this.showMap = true;
        });

      });
    },
    fetchRestaurants() {
      fetch('restaurants.json')
        .then(response => response.json())
        .then(data => {
          this.restaurants = data;
        });
    },
  },
  mounted() {
    this.fetchRestaurants();
  },
};
</script>

<style>
.selected-restaurant {
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-top: 20px;
}

.restaurant-name {
  color: #ff6347; /* A vibrant color for emphasis */
  margin-bottom: 10px;
}

.restaurant-details {
  color: #333;
  font-size: 16px;
  margin-bottom: 5px;
}

.restaurant-rating {
  font-weight: bold;
  margin-bottom: 5px;
}

.visited-status {
  font-size: 16px;
  padding: 5px 10px;
  color: #000000;
  border-radius: 5px;
  display: inline-block; /* Smaller block for status */
  margin-bottom: 10px;
}

.is-visited {
  color: #fff;
  background-color: #4caf50; /* Green for visited */
}


.website-link {
  display: inline-block;
  background-color: #1a73e8;
  color: #fff;
  padding: 10px 15px;
  border-radius: 5px;
  text-decoration: none;
  transition: background-color 0.3s;
}

.website-link:hover {
  background-color: #155fa0;
}


body, html {
  margin: 0;
  padding: 0;
  font-family: 'Lato', sans-serif; /* A softer, more approachable font */
  background-color: #ffe5d9; /* A soft, warm background color */
  color: #444; /* Softer text color for better readability */
}

#app {
  max-width: 800px; /* Adjusted for potentially wider filter controls */
  margin: 40px auto;
  padding: 20px;
  background-color: #fff5ee; /* A light, warm white background for content */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Softer shadow for depth */
  border-radius: 8px; /* Rounded corners for a friendlier look */
  padding: 30px; /* Increased padding for more space inside the app */
}

select[multiple] {
  width: 100%;
  height: auto; /* Adjust based on your design needs */
  overflow-y: auto;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}

h1 {
  font-size: 28px;
  color: #db7093; /* A romantic, soft color */
  margin-bottom: 30px; /* Increased margin for more space around the title */
}

label {
  display: block; /* Make labels block-level for better alignment with selects */
  margin: 10px 0 5px; /* Adjust spacing around labels for clarity */
  font-size: 16px; /* Slightly larger label text for readability */
}

select, button {
  width: 100%; /* Full width controls for a cleaner look on mobile */
  padding: 10px 15px; /* Increased padding for a more tactile feel */
  margin-bottom: 20px; /* Increased margin for better separation between controls */
  font-size: 16px; /* Larger font size for easier interaction */
  border-radius: 20px; /* More pronounced rounded corners */
  border: 2px solid #ff7f50; /* Slightly thicker border with theme color */
}

button {
  color: #fff;
  background-color: #ff7f50; /* A warm, inviting button color */
  border: none;
  font-weight: bold;
}

button:hover {
  background-color: #ff6347; /* A slightly deeper color on hover */
}

#content-wrapper {
  transition: height 0.5s ease-out;
  overflow: hidden; /* Prevents content from overflowing during transition */
}

ul {
  list-style-type: none;
  padding: 0;
  margin-top: 20px; /* Added space above the list for better separation */
}

ul li {
  padding: 15px;
  margin-bottom: 15px; /* Increased margin for more space between items */
  background-color: #fafafa; /* A very light background for list items */
  border-radius: 10px; /* Rounded corners for list items */
  border-left: 5px solid #ff7f50; /* A touch of color to highlight list items */
  transition: background-color 0.3s, border-color 0.3s;
}

ul li.visited {
  border-left-color: #db7093; /* Differentiate visited items with color */
}

ul li:hover {
  background-color: #fff0e6; /* A warm hover effect */
  border-left-color: #ff6347;
}

a {
  color: #db7093; /* Matching the romantic theme */
  font-weight: bold; /* Make links bold for better visibility */
}

a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  #app {
    padding: 20px; /* Adjust padding for smaller screens */
    margin: 20px;
  }

  select, button {
    font-size: 14px; /* Adjust font size for smaller screens */
  }

  h1 {
    font-size: 24px; /* Adjust heading size for smaller screens */
  }
}
</style>
